import { useLayoutEffect, useRef, useState } from 'react';

import { dispensarBanner, TOM_DO_BANNER, useBannerNaTela, type Banner as DadosDoBanner } from '@/lib/banner';
import { useBannerDeVersaoNova, useBannerSemConexao } from '@/lib/banners-do-sistema';
import { useBannerDeSuporte } from '@/modulos/sessao/banner';

import { Botao, BotaoFechar } from './Botao';

/**
 * O BANNER DA MESA, NO FLUXO (R06, p.67 — substitui a regra de flutuar da
 * p.33): entra entre a barra superior e o cabeçalho da página, 12 px abaixo da
 * barra e 16 px acima do cabeçalho, até 720 px, e EMPURRA o conteúdo. Assim
 * nunca cobre título, botões ou o primeiro cartão. Um por vez — o mais grave —,
 * e quem decide qual é `lib/banner`; os outros vão à central de avisos (o sino).
 *
 * No máximo duas linhas (p.67): o texto que passa disso fica cortado, e "Ver"
 * abre o texto completo ali mesmo.
 *
 * "Sem conexão" é o único sem ✕ (p.67), e tem o desenho da rede caída: a
 * barra escura. Os outros são o cartão branco com o bloco do ícone na tinta
 * do tom.
 *
 * As condições do sistema (versão nova, rede caída, sessão de suporte) são
 * vigiadas aqui, porque valem para toda tela; as de negócio, a tela que as
 * conhece registra.
 *
 * Leitor de tela: `polite`; o crítico, `assertive` (p.33). As duas regiões
 * ficam sempre montadas — região criada junto com o texto não é anunciada.
 * Entra descendo 8 px e sai no sentido contrário (`--mov-entrada`, p.21); com
 * redução de movimento, só esmaece.
 */
export function Banner() {
  useBannerDeVersaoNova('/index.html');
  useBannerSemConexao();
  useBannerDeSuporte();
  const { banner, saindo, aoTerminarAnimacao } = useBannerNaTela();
  const cartao = banner ? (
    <Cartao key={banner.id} banner={banner} saindo={saindo} aoTerminarAnimacao={aoTerminarAnimacao} />
  ) : null;
  const critico = banner?.gravidade === 'critico';
  return (
    <div data-banner-no-fluxo="" className="banner-no-fluxo shrink-0">
      <div aria-live="polite">{critico ? null : cartao}</div>
      <div aria-live="assertive">{critico ? cartao : null}</div>
    </div>
  );
}

function Cartao({
  banner,
  saindo,
  aoTerminarAnimacao,
}: {
  readonly banner: DadosDoBanner;
  readonly saindo: boolean;
  readonly aoTerminarAnimacao: () => void;
}) {
  const tom = TOM_DO_BANNER[banner.gravidade];
  const Icone = banner.icone;
  const escuro = banner.gravidade === 'critico';
  const texto = useRef<HTMLParagraphElement>(null);
  const [cortado, setCortado] = useState(false);
  const [completo, setCompleto] = useState(false);

  /* Duas linhas no máximo (p.67): mede se o texto passou delas. */
  useLayoutEffect(() => {
    const p = texto.current;
    if (!p || completo) return;
    const medir = () => {
      setCortado(p.scrollHeight > p.clientHeight + 1);
    };
    medir();
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    observador?.observe(p);
    return () => observador?.disconnect();
  }, [banner.texto, completo]);

  return (
    <div
      data-banner={banner.gravidade}
      onAnimationEnd={(evento) => {
        if (evento.target === evento.currentTarget) aoTerminarAnimacao();
      }}
      className={`${saindo ? 'anima-banner-saida' : 'anima-banner'} flex w-full max-w-[var(--banner-max)] items-center gap-3 rounded-painel py-2 pr-2 pl-3 ${
        escuro ? 'border border-preto-obra bg-preto-obra text-branco' : 'border border-borda-sutil bg-flutuante text-tinta shadow-superficie'
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex size-8 shrink-0 items-center justify-center rounded-controle ${escuro ? '' : tom.bloco}`}
      >
        <Icone className={`size-[var(--icone-card)] ${escuro ? 'text-erro-fundo' : tom.icone}`} />
      </span>
      <p ref={texto} className={`txt-apoio min-w-0 flex-1 ${completo ? '' : 'line-clamp-2'}`}>
        {banner.texto}
      </p>
      {cortado || completo ? (
        <Botao
          nivel="fantasma"
          tamanho="p"
          className={`shrink-0 ${escuro ? 'text-branco' : ''}`}
          aria-expanded={completo}
          onClick={() => {
            setCompleto((c) => !c);
          }}
        >
          {completo ? 'Ver menos' : 'Ver'}
        </Botao>
      ) : null}
      {banner.acao ? (
        <Botao nivel="secundario" tamanho="p" className="shrink-0" onClick={banner.acao.aoAcionar}>
          {banner.acao.rotulo}
        </Botao>
      ) : null}
      {/* "Sem conexão" é o único sem ✕ (p.67): o crítico não fecha enquanto a condição existir. */}
      {banner.gravidade === 'critico' ? null : (
        <BotaoFechar
          rotulo={banner.rotuloDeFechar ?? 'Fechar o aviso'}
          aoFechar={() => {
            dispensarBanner(banner.id);
          }}
        />
      )}
    </div>
  );
}
